import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { useDebouncedCallback } from "use-debounce";
//#endregion
//#region types
import { MultipleAutoCompleteInputProps } from "models/propTypes/inputPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FieldConstants, IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { DialogEnums } from "models/enums";
//#endregion
//#region components
import {
  Chip,
  Checkbox,
  TextField,
  Grid,
  CircularProgress,
  Autocomplete,
  AutocompleteChangeReason,
  InputAdornment,
} from "@mui/material";
import Icon from "../../icon";
import GridR from "../../grid/responsiveGrid";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const MultipleAutoCompleteInput: React.FC<MultipleAutoCompleteInputProps> = (props) => {
  const selectedItems = props.value;
  const [inputValue, setInputValue] = React.useState<string>("");

  const [options, setOptions] = React.useState<AutocompleteListItem[]>();
  const [currentAction, setCurrentAction] = React.useState<AutocompleteListItem>();

  const [loading, setLoading] = React.useState<boolean>(false);

  const itemNone: AutocompleteListItem = {
    value: UtilsHelper.getSelectionNoneText(),
  };

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const getSuggestedItems = (): AutocompleteListItem[] => {
    const suggestedItems = props.includeNone ? [itemNone, ...(props.suggestedItems ?? [])] : props.suggestedItems;
    return (
      suggestedItems?.map((x) => ({
        ...x,
        group: x.group ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
      })) ?? []
    );
  };

  const setDefaultOptions = () => {
    searchItems("").then((items) => {
      if (
        JSON.stringify(
          (items ?? []).map((a) => {
            return { id: a.id, val: a.value };
          })
        ) !==
        JSON.stringify(
          (options ?? []).map((a) => {
            return { id: a.id, val: a.value };
          })
        )
      )
        setOptions(items);
    });
  };

  const searchItems = async (value: string) => {
    let searchItems: AutocompleteListItem[] = [];

    if (value === "" && props.suggestedItems?.length) {
      searchItems = getSuggestedItems();
    } else {
      if (props.items)
        if (typeof props.items === "function") {
          searchItems = await props.items(value, props.format ? UtilsHelper.applyMask(value, props.format) : undefined);
        } else {
          const items = props.includeNone ? [itemNone, ...(props.items ?? [])] : (props.items ?? []);
          searchItems = value
            ? items
                .map((x) => ({
                  index: `${x.value} ${x.description ?? ""}`.toLowerCase().indexOf(value.toLowerCase()),
                  item: x,
                }))
                .filter((x) => x.index >= 0)
                .sort((a, b) => a.index - b.index)
                .map((x) => x.item)
            : items;
        }
    }
    return searchItems;
  };

  const onChange = (event: any, newValue: (AutocompleteListItem | string)[], reason: AutocompleteChangeReason) => {
    setLoading(false);
    const formattedValue = newValue?.filter((i) => typeof i !== "string") as AutocompleteListItem[];
    props.onItemsSelect(formattedValue);
  };

  const debouncedInputChange = useDebouncedCallback((value: string, reason: string) => {
    searchItems(value)
      .then((items) => setOptions(items))
      .finally(() => setLoading(false));
  }, FieldConstants.DebounceTime);

  const onInputChange = (event: object, value: string, reason: string) => {
    setLoading(true);

    switch (reason) {
      case "input":
        setInputValue(value);
        debouncedInputChange(value, reason);
        break;
      case "clear":
        setInputValue("");
        break;
    }
  };

  const onOpen = () => {
    setDefaultOptions();
  };

  const onClose = () => {
    setInputValue("");
    props.onClose && props.onClose();
  };

  const onActionClicked = (option: AutocompleteListItem, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    setLoading(true);
    setCurrentAction(option);

    if (option.form) {
      dispatch(
        displayDialog({
          isOpen: false,
          id: option.title ?? "autoCompleteActionDialog",
          title: option.title ?? "",
          subTitle: option.subtitle ?? "",
          titleIcon: option.formIcon,
          dialogType: DialogEnums.DialogType.Content,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
            },
          ],
          children: option.form,
          onSubmit: (value: any) => {
            setLoading(false);
            setCurrentAction(undefined);
            props.onItemsSelect(value as AutocompleteListItem[]);
          },
          onClose: () => {
            setCurrentAction(undefined);
            setLoading(false);
          },
          maxWidth: "sm",
        })
      );
    } else {
      option.onActionClicked &&
        option.onActionClicked(inputValue.toString()).then((res) => {
          setLoading(false);
          if (res) {
            option.onActionSuccess && option.onActionSuccess(res);
            if (option.actionSuccessMessage) {
              setTimeout(() => {
                setActionResult(res);
              }, 500);
            } else {
              setActionResult(res);
            }
          }
        });
    }
  };

  const setActionResult = (result: AutocompleteListItem) => {
    setCurrentAction(undefined);
    props.onItemsSelect([result]);
  };

  const handleRemove = (item: AutocompleteListItem) => {
    const newSelection = selectedItems?.filter((x) => x.id !== item.id) ?? [];

    if (props.onItemDelete) {
      props.onItemDelete(newSelection, item);
    } else {
      props.onItemsSelect(newSelection);
    }
  };

  const onOptionSelect = (item: AutocompleteListItem, selected: boolean, event: React.MouseEvent<HTMLDivElement>) => {
    if (selected) {
      event.stopPropagation();
      handleRemove(item);
    } else if (props.forceSingleSelection) {
      event.stopPropagation();
      props.onItemsSelect([item]);
    }
  };

  return (
    <Autocomplete
      multiple
      id={`multipleAutoComplete-${props.name}`}
      options={(options ?? getSuggestedItems()).concat(props.actions ?? [])}
      getOptionLabel={(option) => (typeof option !== "string" ? `${option.value} ${option.description}` : "")}
      // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if fixed
      isOptionEqualToValue={(option, value) => option.id == value.id}
      value={selectedItems}
      renderOption={(htmlProps, option) => {
        const matches = match(option.value, inputValue.toString());
        const parts = parse(option.value, matches);
        const selectedAction = currentAction && currentAction?.id === option.id;
        // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if fixed
        const selected = selectedItems?.find((s) => option.id == s.id) !== undefined;

        return (
          <li {...htmlProps} key={option.id}>
            {option.onActionClicked || option.form ? (
              <Grid
                onClick={onActionClicked.bind(null, option)}
                className={clsx(cssExports.actionItemContainer, option.className)}
              >
                <Grid container className={clsx(cssExports.actionTitleOuter)}>
                  <Grid item>
                    {selectedAction && loading ? (
                      <CircularProgress size={20} className={cssExports.buttonProgressWhite} />
                    ) : selectedAction ? (
                      <span className={clsx(cssExports.actionIcon, cssExports.success)}>
                        <Icon icon={IconDefinitionConstants.WCheck} />
                      </span>
                    ) : option.icon ? (
                      <span className={cssExports.actionIcon}>
                        <Icon icon={option.icon} />
                      </span>
                    ) : (
                      <></>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Grid item className={cssExports.actionTitle}>
                      {currentAction?.actionSuccessMessage && selectedAction && !loading
                        ? currentAction.actionSuccessMessage
                        : option.value.replace(FieldConstants.ValueTemplateText, inputValue.toString() ?? "")}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid container className={option.className} onClick={onOptionSelect.bind(null, option, selected)}>
                <Grid container wrap="nowrap">
                  <Checkbox checked={selected} className={cssExports.checkBoxOption} />
                  {option.icon ? (
                    <span className={cssExports.icon}>
                      <Icon icon={option.icon} />
                    </span>
                  ) : (
                    <></>
                  )}
                  <span
                    className={clsx(
                      cssExports.optionTextContainer,
                      option.icon ? cssExports.hasIcon : "",
                      cssExports.hasCheckBox
                    )}
                  >
                    <span className={cssExports.optionText}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          className={clsx(
                            part.highlight ? cssExports.highlightText : "",
                            option.description ? cssExports.withSubText : ""
                          )}
                        >
                          {part.text}
                        </span>
                      ))}

                      {props.valueSuffix ? (
                        <span
                          className={clsx(option.description ? cssExports.withSubText : "")}
                        >{` ${props.valueSuffix}`}</span>
                      ) : (
                        ""
                      )}
                    </span>
                    {option.description && <span className={cssExports.subText}>{option.description}</span>}
                  </span>
                </Grid>
              </Grid>
            )}
          </li>
        );
      }}
      classes={{
        root: clsx(cssExports.autoCompleteRoot, cssExports.multipleAutoCompleteInput),
        popper: clsx(cssExports.autoCompletePopper, props.classes?.popper),
        paper: clsx(cssExports.autoCompleteContainer, cssExports.checkBoxOption),
        option: cssExports.autoCompleteOption,
        groupLabel: cssExports.autoCompleteGroupTitle,
        noOptions: cssExports.autoCompleteNoOptions,
        inputRoot: clsx(
          props.disabled || props.success || props.error || (props.showLoading && loading)
            ? cssExports.autoCompleteInputWithEndAdornment
            : "",
          cssExports.autoCompleteInputRoot
        ),
        endAdornment: cssExports.autoCompleteEndAdornment,
        clearIndicator: cssExports.autoCompleteClearIndicator,
        popperDisablePortal: cssExports.popperDisabledPortal,
        popupIndicator: cssExports.autoCompletePopUpIndicator,
      }}
      renderInput={(params) => {
        const placeholderText = React.isValidElement(props.placeholder)
          ? (props.label?.toString() ?? "")
          : (props.placeholder?.toString() ?? props.label?.toString() ?? "");

        return (
          <span className={clsx(cssExports.iconInputContainer)}>
            <TextField
              {...params}
              placeholder={placeholderText}
              variant="outlined"
              error={props.error}
              fullWidth
              InputProps={{
                ...params.InputProps,
                classes: {
                  root: cssExports.inputRoot,
                  input: clsx(cssExports.inputInput, selectedItems?.length ? cssExports.hasValue : ""),
                  focused: cssExports.focusedInput,
                  error: cssExports.errorInput,
                  disabled: cssExports.disabledInput,
                  notchedOutline: cssExports.notchedOutline,
                },
                endAdornment: (
                  <>
                    {props.disabled ? (
                      <InputAdornment
                        position="end"
                        className={clsx(cssExports.inputAdornedEnd, cssExports.disabledIcon)}
                      >
                        <Icon icon={IconDefinitionConstants.WLock} className={cssExports.inputIcon} />
                      </InputAdornment>
                    ) : props.success ? (
                      <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.success)}>
                        <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
                      </InputAdornment>
                    ) : props.error ? (
                      <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.error)}>
                        <Icon icon={IconDefinitionConstants.WExclamation} className={cssExports.inputIcon} />
                      </InputAdornment>
                    ) : (
                      <></>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{
                shrink: false,
                classes: {
                  root: clsx(cssExports.inputLabel),
                  error: cssExports.errorLabel,
                  shrink: cssExports.shrinkedLabel,
                  focused: cssExports.focusedLabel,
                },
              }}
            />
          </span>
        );
      }}
      renderTags={(value: AutocompleteListItem[], getTagProps) => (
        <Grid item container className={clsx(props.className, cssExports.selectionChipsGroup)}>
          <GridR item container className={cssExports.selectionChips} spacing={1} direction="row">
            {value.map((item, index: number) => (
              <Grid item key={item.id} className={cssExports.chipContainer}>
                <Chip
                  {...getTagProps({ index })}
                  key={item.id}
                  className={clsx(cssExports.chip, cssExports.selected)}
                  variant={"outlined"}
                  data-key={item.id}
                  label={
                    <Grid container direction="row" wrap="nowrap">
                      <Grid item xs className={cssExports.chipLabel}>
                        {item.value}
                      </Grid>
                      {item.selectedSubText ? (
                        <Grid item>
                          <label className={cssExports.selectedSubText}>({item.selectedSubText})</label>
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  }
                  onDelete={
                    props.disabled
                      ? undefined
                      : () => {
                          handleRemove(item);
                        }
                  }
                />
              </Grid>
            ))}
          </GridR>
        </Grid>
      )}
      renderGroup={(option) => (
        <li key={option.key}>
          {!UtilsHelper.isEmpty(option.key) ? (
            <Grid container direction="row" className={cssExports.autoCompleteGroupTitle}>
              <Grid item xs>
                {props.groupLabelFormatter ? props.groupLabelFormatter(option.key) : option.group}
              </Grid>
              {props.groupRightContent && props.groupRightContent(option.key)}
            </Grid>
          ) : (
            <></>
          )}
          <ul>{option.children}</ul>
        </li>
      )}
      groupBy={(option) => (option.group === undefined ? "" : `${option.group}`)}
      getOptionDisabled={(option) =>
        !!option.disabled || (currentAction !== undefined && currentAction?.id === option.id)
      }
      inputValue={inputValue}
      disableCloseOnSelect
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      onInputChange={onInputChange}
      disabled={props.disabled}
    />
  );
};

export default MultipleAutoCompleteInput;
